import { useState, useEffect } from 'react';
import { Dimensions, Platform } from 'react-native';

function getWindowDimensions(): { width: number; height: number } {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return { width: window.innerWidth, height: window.innerHeight };
  }
  const { width, height } = Dimensions.get('window');
  return { width, height };
}

export const useResponsiveLayout = () => {
  const [screenWidth, setScreenWidth] = useState(
    () => getWindowDimensions().width
  );
  const [screenHeight, setScreenHeight] = useState(
    () => getWindowDimensions().height
  );

  useEffect(() => {
    const update = () => {
      const { width, height } = getWindowDimensions();
      setScreenWidth(width);
      setScreenHeight(height);
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const subscription = Dimensions.addEventListener(
      'change',
      ({ window: w }) => {
        setScreenWidth(w.width);
        setScreenHeight(w.height);
      }
    );
    return () => subscription?.remove();
  }, []);

  // Enhanced breakpoints for better mobile responsiveness
  const isMobile = Platform.OS !== 'web' || screenWidth < 768;
  const isWeb = Platform.OS === 'web' && screenWidth >= 768;

  // More granular mobile breakpoints
  const isSmallMobile = screenWidth < 375;
  const isMediumMobile = screenWidth >= 375 && screenWidth < 414;
  const isLargeMobile = screenWidth >= 414 && screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const isDesktop = screenWidth >= 1024;

  // Dynamic container width based on screen size
  let containerWidth: string | number;
  let containerPadding: number;

  if (isSmallMobile) {
    containerWidth = '95%';
    containerPadding = 12;
  } else if (isMediumMobile) {
    containerWidth = '92%';
    containerPadding = 16;
  } else if (isLargeMobile) {
    containerWidth = '90%';
    containerPadding = 20;
  } else if (isTablet) {
    containerWidth = '90%';
    containerPadding = 24;
  } else if (isDesktop) {
    containerWidth = '85%';
    containerPadding = 32;
  } else {
    containerWidth = '100%';
    containerPadding = 16;
  }

  return {
    screenWidth,
    screenHeight,
    isMobile,
    isWeb,
    isSmallMobile,
    isMediumMobile,
    isLargeMobile,
    isTablet,
    isDesktop,
    containerWidth,
    containerPadding,
  };
};
